import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  useTheme,
  alpha,
  Skeleton,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  Stack,
  Divider,
  useMediaQuery
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Lightbulb as LightbulbIcon,
  Assessment as AssessmentIcon,
  ExpandMore as ExpandMoreIcon,
  LocationOn as LocationOnIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarTodayIcon,
  Stars as StarsIcon,
  Analytics as AnalyticsIcon,
  Security as SecurityIcon,
  Insights as InsightsIcon,
  PieChart as PieChartIcon
} from '@mui/icons-material';
import { formatCurrency } from '@utils/financialUtils';
import { Hotel } from '../../../types/types';

interface PortfolioStats {
  totalProperties: number;
  totalRooms: number;
  totalValue: number;
  averageStarRating: number;
  totalRenovationCost: number;
  averageOccupancy: number;
  revenueGrowth: number;
  portfolioAge: number;
}

interface PortfolioInsightsProps {
  hotels: Hotel[];
  stats: PortfolioStats;
  loading?: boolean;
  currency?: 'USD' | 'EUR';
}

interface Insight {
  type: 'success' | 'warning' | 'info' | 'error';
  title: string;
  description: string;
  action?: string;
  impact: 'high' | 'medium' | 'low';
}

const PortfolioInsights: React.FC<PortfolioInsightsProps> = ({
  hotels,
  stats,
  loading = false,
  currency = 'USD'
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Generate insights based on portfolio data
  const generateInsights = (): Insight[] => {
    const insights: Insight[] = [];

    // Portfolio diversification insight
    const locations = new Set(hotels.map(hotel => hotel.city));
    if (locations.size === 1 && hotels.length > 1) {
      insights.push({
        type: 'warning',
        title: 'Geographic Concentration Risk',
        description: `All ${hotels.length} properties are located in the same area. Consider diversifying across multiple markets to reduce risk.`,
        action: 'Explore opportunities in other markets',
        impact: 'high'
      });
    }

    // Star rating insight
    if (stats.averageStarRating < 3.5) {
      insights.push({
        type: 'warning',
        title: 'Portfolio Quality Improvement Needed',
        description: `Average star rating of ${stats.averageStarRating.toFixed(1)} is below market standards. Focus on improving service quality and amenities.`,
        action: 'Develop quality improvement plan',
        impact: 'high'
      });
    } else if (stats.averageStarRating >= 4.5) {
      insights.push({
        type: 'success',
        title: 'Excellent Portfolio Quality',
        description: `Outstanding ${stats.averageStarRating.toFixed(1)} average star rating indicates high-quality portfolio positioning.`,
        impact: 'medium'
      });
    }

    // Property age insight
    const oldProperties = hotels.filter(hotel => {
      const yearBuilt = hotel.year_built || new Date().getFullYear();
      return (new Date().getFullYear() - yearBuilt) > 30;
    });

    if (oldProperties.length > 0) {
      insights.push({
        type: 'info',
        title: 'Aging Properties Identified',
        description: `${oldProperties.length} properties are over 30 years old. Consider modernization investments to maintain competitiveness.`,
        action: 'Schedule property assessments',
        impact: 'medium'
      });
    }

    // Revenue growth insight
    if (stats.revenueGrowth > 10) {
      insights.push({
        type: 'success',
        title: 'Strong Revenue Growth',
        description: `${stats.revenueGrowth.toFixed(1)}% revenue growth significantly exceeds industry averages. Portfolio is performing exceptionally well.`,
        impact: 'low'
      });
    } else if (stats.revenueGrowth < 0) {
      insights.push({
        type: 'error',
        title: 'Revenue Decline Alert',
        description: `Negative revenue growth of ${stats.revenueGrowth.toFixed(1)}% requires immediate attention and strategic intervention.`,
        action: 'Conduct performance analysis',
        impact: 'high'
      });
    }

    return insights;
  };

  const insights = generateInsights();

  const getInsightIcon = (type: string) => {
    switch (type) {
      case 'success':
        return <CheckCircleIcon />;
      case 'warning':
        return <WarningIcon />;
      case 'error':
        return <WarningIcon />;
      default:
        return <InfoIcon />;
    }
  };

  const getInsightColor = (type: string) => {
    switch (type) {
      case 'success':
        return theme.palette.success.main;
      case 'warning':
        return theme.palette.warning.main;
      case 'error':
        return theme.palette.error.main;
      default:
        return theme.palette.info.main;
    }
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'high':
        return theme.palette.error.main;
      case 'medium':
        return theme.palette.warning.main;
      default:
        return theme.palette.info.main;
    }
  };

  // Helper functions for insights
  const getPortfolioQuality = (): { score: string; description: string; color: string } => {
    if (!hotels.length) return { score: 'N/A', description: 'No data', color: theme.palette.text.secondary };

    const avgRating = hotels.reduce((sum, hotel) => sum + (hotel.star_rating || 0), 0) / hotels.length;
    const avgAge = hotels.reduce((sum, hotel) => {
      const age = hotel.year_built ? new Date().getFullYear() - hotel.year_built : 50;
      return sum + age;
    }, 0) / hotels.length;

    if (avgRating >= 4 && avgAge < 20) return { score: 'Excellent', description: 'High-quality modern portfolio', color: theme.palette.success.main };
    if (avgRating >= 3.5 && avgAge < 30) return { score: 'Good', description: 'Well-maintained properties', color: theme.palette.info.main };
    if (avgRating >= 3 && avgAge < 40) return { score: 'Fair', description: 'Average market position', color: theme.palette.warning.main };
    return { score: 'Needs Attention', description: 'Improvement required', color: theme.palette.error.main };
  };

  const getGeographicDiversification = (): { score: string; description: string; color: string; percentage: number } => {
    const uniqueStates = new Set(hotels.map(hotel => hotel.state)).size;
    const uniqueCities = new Set(hotels.map(hotel => `${hotel.city}, ${hotel.state}`)).size;
    const diversificationRatio = uniqueCities / hotels.length;

    if (uniqueStates >= 3) return {
      score: 'Well Diversified',
      description: `${uniqueStates} states, ${uniqueCities} cities`,
      color: theme.palette.success.main,
      percentage: Math.min(diversificationRatio * 100, 100)
    };
    if (uniqueStates >= 2 || uniqueCities >= 3) return {
      score: 'Moderately Diversified',
      description: `${uniqueStates} states, ${uniqueCities} cities`,
      color: theme.palette.warning.main,
      percentage: diversificationRatio * 100
    };
    return {
      score: 'Concentrated',
      description: `${uniqueStates} states, ${uniqueCities} cities`,
      color: theme.palette.error.main,
      percentage: diversificationRatio * 100
    };
  };

  const getRiskProfile = (): { score: string; description: string; color: string; percentage: number } => {
    const locationConcentration = new Set(hotels.map(hotel => `${hotel.city}, ${hotel.state}`)).size / hotels.length;
    const ageRisk = hotels.filter(hotel => {
      const age = hotel.year_built ? new Date().getFullYear() - hotel.year_built : 50;
      return age > 40;
    }).length / hotels.length;

    const riskScore = (1 - locationConcentration) * 0.6 + ageRisk * 0.4;

    if (riskScore > 0.6) return {
      score: 'High Risk',
      description: 'Concentration & aging concerns',
      color: theme.palette.error.main,
      percentage: riskScore * 100
    };
    if (riskScore > 0.3) return {
      score: 'Medium Risk',
      description: 'Some risk factors present',
      color: theme.palette.warning.main,
      percentage: riskScore * 100
    };
    return {
      score: 'Low Risk',
      description: 'Well-balanced portfolio',
      color: theme.palette.success.main,
      percentage: riskScore * 100
    };
  };

  const getInvestmentLevel = (): { score: string; description: string; color: string; percentage: number } => {
    const renovatedRecently = hotels.filter(hotel =>
      hotel.renovation_history?.some(renovation =>
        renovation.year >= new Date().getFullYear() - 5
      )
    ).length;

    const percentage = (renovatedRecently / hotels.length) * 100;

    if (percentage >= 60) return {
      score: 'High Investment',
      description: `${renovatedRecently} properties renovated recently`,
      color: theme.palette.success.main,
      percentage
    };
    if (percentage >= 30) return {
      score: 'Active Updates',
      description: `${renovatedRecently} properties renovated recently`,
      color: theme.palette.info.main,
      percentage
    };
    if (percentage >= 10) return {
      score: 'Some Investment',
      description: `${renovatedRecently} properties renovated recently`,
      color: theme.palette.warning.main,
      percentage
    };
    return {
      score: 'Low Investment',
      description: 'Limited recent renovations',
      color: theme.palette.error.main,
      percentage
    };
  };

  const portfolioQuality = getPortfolioQuality();
  const geographicDiv = getGeographicDiversification();
  const riskProfile = getRiskProfile();
  const investmentLevel = getInvestmentLevel();

  // Professional metric card for insights
  const InsightMetricCard: React.FC<{
    title: string;
    score: string;
    description: string;
    color: string;
    percentage?: number;
    icon: React.ElementType;
    loading?: boolean;
  }> = ({ title, score, description, color, percentage, icon: Icon, loading = false }) => (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: 3,
        border: `1px solid ${alpha(color, 0.2)}`,
        backgroundColor: alpha(color, 0.02),
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          borderColor: color,
          backgroundColor: alpha(color, 0.05),
          transform: 'translateY(-2px)',
          boxShadow: `0 4px 12px ${alpha(color, 0.15)}`
        }
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {loading ? (
          <Box>
            <Skeleton variant="circular" width={40} height={40} sx={{ mb: 2 }} />
            <Skeleton variant="text" width="60%" height={24} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="80%" height={16} />
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Icon sx={{ fontSize: 32, color }} />
              {percentage !== undefined && (
                <Typography variant="h6" sx={{ fontWeight: 700, color }}>
                  {percentage.toFixed(0)}%
                </Typography>
              )}
            </Box>

            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: theme.palette.text.primary }}>
              {score}
            </Typography>

            <Typography variant="caption" color="text.secondary" sx={{
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              display: 'block',
              mb: 1
            }}>
              {title}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.4 }}>
              {description}
            </Typography>

            {percentage !== undefined && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={percentage}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: alpha(color, 0.1),
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: color,
                      borderRadius: 3
                    }
                  }}
                />
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box>
      {loading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {[...Array(3)].map((_, index) => (
            <Skeleton key={index} variant="rectangular" height={160} sx={{ borderRadius: 2 }} />
          ))}
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Portfolio Analytics Overview */}
          <Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AnalyticsIcon sx={{ color: theme.palette.primary.main, fontSize: 18 }} />
                Portfolio Analytics
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Key performance metrics and risk analysis
              </Typography>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} lg={3}>
                <InsightMetricCard
                  title="Portfolio Quality"
                  score={portfolioQuality.score}
                  description={portfolioQuality.description}
                  color={portfolioQuality.color}
                  icon={StarsIcon}
                  loading={loading}
                />
              </Grid>

              <Grid item xs={12} sm={6} lg={3}>
                <InsightMetricCard
                  title="Geographic Spread"
                  score={geographicDiv.score}
                  description={geographicDiv.description}
                  color={geographicDiv.color}
                  percentage={geographicDiv.percentage}
                  icon={LocationOnIcon}
                  loading={loading}
                />
              </Grid>

              <Grid item xs={12} sm={6} lg={3}>
                <InsightMetricCard
                  title="Investment Activity"
                  score={investmentLevel.score}
                  description={investmentLevel.description}
                  color={investmentLevel.color}
                  percentage={investmentLevel.percentage}
                  icon={TrendingUpIcon}
                  loading={loading}
                />
              </Grid>

              <Grid item xs={12} sm={6} lg={3}>
                <InsightMetricCard
                  title="Risk Assessment"
                  score={riskProfile.score}
                  description={riskProfile.description}
                  color={riskProfile.color}
                  percentage={riskProfile.percentage}
                  icon={SecurityIcon}
                  loading={loading}
                />
              </Grid>
            </Grid>
          </Box>

          {/* AI-Powered Insights */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`
            }}
          >
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <InsightsIcon sx={{ color: theme.palette.primary.main, fontSize: 18 }} />
                AI-Powered Insights
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Smart recommendations based on portfolio analysis
              </Typography>
            </Box>

            {insights.length > 0 ? (
              <Grid container spacing={2}>
                {insights.map((insight, index) => (
                  <Grid item xs={12} md={6} key={index}>
                    <Card
                      elevation={0}
                      sx={{
                        height: '100%',
                        borderRadius: 1,
                        border: `1px solid ${alpha(getInsightColor(insight.type), 0.2)}`,
                        backgroundColor: alpha(getInsightColor(insight.type), 0.03),
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          borderColor: getInsightColor(insight.type),
                          backgroundColor: alpha(getInsightColor(insight.type), 0.06),
                        }
                      }}
                    >
                      <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                          <Box
                            sx={{
                              p: 0.75,
                              borderRadius: 1,
                              backgroundColor: alpha(getInsightColor(insight.type), 0.1),
                              color: getInsightColor(insight.type)
                            }}
                          >
                            {getInsightIcon(insight.type)}
                          </Box>

                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {insight.title}
                              </Typography>
                              <Chip
                                label={insight.impact.toUpperCase()}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: '0.6rem',
                                  fontWeight: 700,
                                  backgroundColor: alpha(getImpactColor(insight.impact), 0.1),
                                  color: getImpactColor(insight.impact)
                                }}
                              />
                            </Box>

                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, lineHeight: 1.4 }}>
                              {insight.description}
                            </Typography>

                            {insight.action && (
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  color: getInsightColor(insight.type),
                                  fontSize: '0.875rem'
                                }}
                              >
                                💡 {insight.action}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  textAlign: 'center',
                  backgroundColor: alpha(theme.palette.background.default, 0.5),
                  borderRadius: 1,
                  border: `1px dashed ${theme.palette.divider}`
                }}
              >
                <LightbulbIcon sx={{ fontSize: 32, color: theme.palette.text.disabled, mb: 1.5 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Generating Insights
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Add more properties to unlock personalized AI insights
                </Typography>
              </Paper>
            )}
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default PortfolioInsights;